// Echo-Brain API Integration Service
// Connects AIDELINE with brain.echomotion.ai

const ECHO_BRAIN_API_BASE =
  import.meta.env.VITE_ECHO_BRAIN_API_BASE || '/api/echo-brain';
// CRM - Business Hub project ID in Echo-Brain
const CRM_PROJECT_ID = 'project-d3c5ec12-c9d1-4abd-90ae-796e7a5b4a4d';

const CHECKLIST_NAMES = {
  bug: 'Bugs-feedback',
  change_request: 'Änderungen-feedback',
  feature_request: 'Features-feedback',
} as const;

type FeedbackType = 'bug' | 'change_request' | 'feature_request';

export interface EchoBrainProject {
  id: string;
  name: string;
  status?: string;
  lifecycle_stage?: string;
  priority?: string;
  health_status?: string;
}

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const getHeaders = {
  Accept: 'application/json',
};

function echoBrainUrl(path: string, params?: Record<string, string | undefined>): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(`${ECHO_BRAIN_API_BASE.replace(/\/$/, '')}${path}`, origin);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

async function parseEchoBrainResponse<T>(res: Response, fallbackMessage: string): Promise<T> {
  const text = await res.text();
  let payload: any = null;

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: text };
    }
  }

  if (!res.ok) {
    throw new Error(payload?.error || payload?.message || fallbackMessage);
  }

  return payload as T;
}

function normalizeEchoBrainProjects(payload: any): EchoBrainProject[] {
  const projects = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.projects)
      ? payload.projects
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.projects)
          ? payload.data.projects
          : Array.isArray(payload?.items)
            ? payload.items
            : [];

  return projects
    .map((project: any) => ({
      id: String(project.id || project.project_id || project.uuid || ''),
      name: String(project.name || project.title || project.id || project.project_id || 'Untitled project'),
      status: project.status,
      lifecycle_stage: project.lifecycle_stage,
      priority: project.priority,
      health_status: project.health_status,
    }))
    .filter((project: EchoBrainProject) => project.id);
}

export const echoBrainService = {
  async getProjects(): Promise<EchoBrainProject[]> {
    const res = await fetch(echoBrainUrl('/projects'), { headers: getHeaders });
    const payload = await parseEchoBrainResponse<any>(res, 'Failed to fetch Echo-Brain projects');
    return normalizeEchoBrainProjects(payload);
  },

  // Post feedback item to Echo-Brain CRM project
  async postFeedback(type: FeedbackType, text: string, source = 'AIDELINE') {
    const res = await fetch(echoBrainUrl('/checklist-items'), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        checklist_name: CHECKLIST_NAMES[type],
        text,
        type,
        source,
      }),
    });
    return parseEchoBrainResponse(res, 'Failed to post to Echo-Brain');
  },

  // Get all feedback checklists from CRM project
  async getFeedbackChecklists() {
    const res = await fetch(echoBrainUrl('/checklist-items'), { headers: getHeaders });
    return parseEchoBrainResponse(res, 'Failed to fetch from Echo-Brain');
  },

  // Get items from a specific checklist
  async getChecklistItems(checklistName: string) {
    const res = await fetch(
      echoBrainUrl('/checklist-items', { checklist: checklistName }),
      { headers: getHeaders }
    );
    return parseEchoBrainResponse(res, 'Failed to fetch checklist');
  },

  // Get CRM project overview
  async getCRMProject() {
    const res = await fetch(echoBrainUrl('/project'), { headers: getHeaders });
    return parseEchoBrainResponse(res, 'Failed to fetch project');
  },

  // Sync a technical task to Echo-Brain
  async syncTask(task: {
    id: string;
    title: string;
    description?: string;
    assigned_to?: string;
    assigned_to_name?: string;
    deadline?: string | null;
    priority?: 'low' | 'medium' | 'high';
    category?: string;
    source?: string;
    project_id?: string;
  }) {
    const path = task.project_id
      ? `/projects/${encodeURIComponent(task.project_id)}/tasks`
      : '/tasks';
    const res = await fetch(echoBrainUrl(path), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({
        title: task.title,
        description: task.description || null,
        assigned_to: task.assigned_to || null,
        assigned_to_name: task.assigned_to_name || null,
        deadline: task.deadline || null,
        priority: task.priority || 'medium',
        category: task.category || 'technical',
        source: task.source || 'AIDELINE',
        source_task_id: task.id,
      }),
    });
    return parseEchoBrainResponse(res, 'Failed to sync task to Echo-Brain');
  },

  // Get synced tasks from Echo-Brain CRM project
  async getSyncedTasks(category?: string) {
    const res = await fetch(echoBrainUrl('/tasks', { category }), { headers: getHeaders });
    return parseEchoBrainResponse(res, 'Failed to fetch tasks from Echo-Brain');
  },

  CHECKLIST_NAMES,
  CRM_PROJECT_ID,
  ECHO_BRAIN_URL: 'https://brain.echomotion.ai',
};
